import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import { convertCartToInr, getCartItems, getConvertToInrPreview } from '../../../../redux/features/cartSlice';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Cart's round-off: whole rupees/dirhams (Math.round), matching the backend.
const roundOff = (n) => Math.round(Number(n) || 0);

// Mirror of the backend conversion so the preview updates live as the operator
// edits the rate. Rule: apply the discount first (in AED), round OFF the amount,
// THEN multiply by the rate. AED has no EMI, so there's no processing-fee term.
// admissionFeeOverride: pass the operator's edited INR value (still rounded to
// whole rupees) to use instead of the auto rate-converted admission fee.
const computeConverted = (current, rate, admissionFeeOverride = null) => {
  if (!current || !rate || !isFinite(rate) || rate <= 0) return null;
  const mrpAed = Number(current.mrp) || 0;
  const discountAed = Number(current.discount) || 0;
  const additionalAed = Number(current.additionalAmount) || 0;
  const admissionFeeAed = Number(current.admissionFee) || 0;

  const mrp = roundOff(roundOff(mrpAed) * rate);
  const discount = roundOff(roundOff(discountAed) * rate);
  const additionalAmount = roundOff(roundOff(additionalAed) * rate);
  // Course amount: discounted amount in AED -> round -> multiply by rate.
  // Note this can differ by a rupee or two from (mrp - discount) above, since
  // those two are each rounded independently before conversion.
  const courseAmount = roundOff(roundOff(mrpAed - discountAed) * rate);
  const admissionFee =
    admissionFeeOverride != null
      ? roundOff(admissionFeeOverride)
      : admissionFeeAed > 0
      ? roundOff(roundOff(admissionFeeAed) * rate)
      : 0;
  const netAmount = courseAmount + additionalAmount + admissionFee;
  return { mrp, discount, additionalAmount, admissionFee, courseAmount, netAmount };
};

const fmtAed = (v) => `AED ${Number(v || 0).toLocaleString('en-AE')}`;
const fmtInr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const Row = ({ label, aed, inr }) => (
  <TableRow>
    <TableCell sx={{ fontSize: '0.75rem' }}>{label}</TableCell>
    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
      {fmtAed(aed)}
    </TableCell>
    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'primary.main' }}>
      {inr == null ? '—' : fmtInr(inr)}
    </TableCell>
  </TableRow>
);

const ConvertToInr = ({ item, handleClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rate, setRate] = useState('');
  const [admissionFeeInput, setAdmissionFeeInput] = useState('');
  const [admissionFeeTouched, setAdmissionFeeTouched] = useState(false);

  const leadId =
    location?.state?.leadId || new URLSearchParams(location.search).get('leadId');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPreview(null);
    dispatch(getConvertToInrPreview(item.cartId))
      .then((res) => {
        const payload = res?.payload;
        if (payload?.status === 'success') {
          setPreview(payload.data);
          // Prefill the auto-derived rate; blank if none so the operator must type one.
          setRate(payload.data?.rate != null ? String(round2(payload.data.rate)) : '');
          // Reset the admission fee override — it'll be re-derived (rate * AED fee)
          // by the sync effect below once the rate is known.
          setAdmissionFeeTouched(false);
          setAdmissionFeeInput('');
        } else {
          enqueueSnackbar(payload?.message || 'Could not load conversion preview', { variant: 'error' });
        }
      })
      .finally(() => setLoading(false));
  }, [open, item.cartId, dispatch, enqueueSnackbar]);

  const rateNum = rate === '' ? null : Number(rate);
  const rateValid = rateNum != null && isFinite(rateNum) && rateNum > 0;

  // Auto admission fee (AED admission fee * rate) for the current rate — used both
  // as the field's live default (until the operator edits it) and as the "reset to
  // auto" value shown next to the field.
  const autoAdmissionFee =
    preview && rateValid ? computeConverted(preview.current, rateNum).admissionFee : null;

  // Keep the field synced to the auto value as the rate changes, unless the
  // operator has typed their own value — then it stays pinned until reset.
  useEffect(() => {
    if (admissionFeeTouched || autoAdmissionFee == null) return;
    setAdmissionFeeInput(String(autoAdmissionFee));
  }, [autoAdmissionFee, admissionFeeTouched]);

  const admissionFeeNum = admissionFeeInput === '' ? null : Number(admissionFeeInput);
  const admissionFeeValid =
    admissionFeeNum != null && isFinite(admissionFeeNum) && admissionFeeNum >= 0;

  const converted = useMemo(
    () =>
      preview && rateValid
        ? computeConverted(preview.current, rateNum, admissionFeeValid ? admissionFeeNum : null)
        : null,
    [preview, rateNum, rateValid, admissionFeeNum, admissionFeeValid]
  );

  const eligible = preview?.eligible;

  const handleSubmit = () => {
    if (!rateValid) {
      enqueueSnackbar('Enter a valid conversion rate (₹ per AED)', { variant: 'error' });
      return;
    }
    if (admissionFeeInput !== '' && !admissionFeeValid) {
      enqueueSnackbar('Admission fee must be zero or a positive number', { variant: 'error' });
      return;
    }
    setSubmitting(true);
    dispatch(
      convertCartToInr({
        cartId: item.cartId,
        rate: rateNum,
        // Only send an override when the operator actually edited the field —
        // otherwise the server derives the same auto value itself.
        ...(admissionFeeTouched && admissionFeeValid ? { admissionFee: admissionFeeNum } : {}),
      })
    )
      .then((res) => {
        const payload = res?.payload;
        if (payload?.status === 'success') {
          enqueueSnackbar('Converted to INR successfully', { variant: 'success' });
          if (leadId) dispatch(getCartItems(leadId));
          setOpen(false);
          if (handleClose) handleClose();
        } else {
          enqueueSnackbar(payload?.message || 'Conversion failed', { variant: 'error' });
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <GlobalDialog
      title="Convert to INR"
      open={open}
      setOpen={setOpen}
      closeButton={true}
      fullWidth
      maxWidth="sm"
      popupButton={
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Button variant="outlined" size="small" color="primary" onClick={() => setOpen(true)}>
            <Icon icon="mdi:currency-inr" width="20" height="20" />
            Convert to INR
          </Button>
        </Box>
      }
      content={
        <Box sx={{ p: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !preview ? (
            <Alert severity="error">Could not load conversion details.</Alert>
          ) : !eligible ? (
            <Alert severity="warning">{preview.reason || 'This item cannot be converted.'}</Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Convert this AED enrollment’s fees, payment schedule and invoice to INR. The rate is
                taken from the first payment and can be overridden below. This cannot be undone.
              </Typography>

              <Box>
                <TextField
                  label="Conversion rate (₹ per AED)"
                  type="number"
                  size="small"
                  fullWidth
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  error={rate !== '' && !rateValid}
                  helperText={
                    preview.hasFirstPayment
                      ? `Auto-derived from the first payment${
                          preview.rate != null ? ` (₹${round2(preview.rate)} per AED)` : ''
                        } — edit if needed.`
                      : 'No payment found to auto-derive the rate — please enter it manually.'
                  }
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Box>

              <Divider />

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Field</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                      Current (AED)
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                      After (INR)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <Row label="MRP" aed={preview.current.mrp} inr={converted?.mrp} />
                  <Row label="Discount" aed={preview.current.discount} inr={converted?.discount} />
                  <Row
                    label="Course amount"
                    aed={preview.current.courseAmount}
                    inr={converted?.courseAmount}
                  />
                  <Row
                    label="Additional amount"
                    aed={preview.current.additionalAmount}
                    inr={converted?.additionalAmount}
                  />
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.75rem' }}>Admission fee</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                      {fmtAed(preview.current.admissionFee)}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <TextField
                          size="small"
                          type="number"
                          value={admissionFeeInput}
                          onChange={(e) => {
                            setAdmissionFeeTouched(true);
                            setAdmissionFeeInput(e.target.value);
                          }}
                          disabled={!rateValid}
                          error={admissionFeeInput !== '' && !admissionFeeValid}
                          inputProps={{
                            min: 0,
                            step: '1',
                            style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px 8px' },
                          }}
                          sx={{ width: 110 }}
                        />
                        {admissionFeeTouched && autoAdmissionFee != null && admissionFeeNum !== autoAdmissionFee ? (
                          <Button
                            size="small"
                            sx={{ minWidth: 0, fontSize: '0.65rem', px: 0.5 }}
                            onClick={() => {
                              setAdmissionFeeTouched(false);
                              setAdmissionFeeInput(String(autoAdmissionFee));
                            }}
                          >
                            Auto
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <Row label="Net amount" aed={preview.current.netAmount} inr={converted?.netAmount} />
                </TableBody>
              </Table>

              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                Each AED amount above is rounded to the nearest whole AED before multiplying by the
                rate, so a value here isn&apos;t always exactly (AED × rate) — Course amount, for
                example, can differ by a rupee or two from MRP row minus Discount row. Admission fee
                is auto-converted the same way (AED fee × rate) but can be edited above; click{' '}
                <b>Auto</b> to reset it. Payment schedule and invoice amounts are converted with the
                same rate on the server; recorded payments (income) are left unchanged.
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="small" onClick={() => setOpen(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!rateValid || submitting}
                  startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                >
                  Convert
                </Button>
              </Box>
            </Stack>
          )}
        </Box>
      }
    />
  );
};

export default ConvertToInr;
